import React, { useState, useRef, useEffect } from "react";
import { Bell, Trash2, AlertTriangle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { isTicketNotification, getTicketNavigationPath } from "../../utils/ticketNotificationUtils";

export default function NotificationBell({ notifications, unreadCount, onMarkRead, onMarkAllRead, onClearAll, currentUserId, userRoles = [] }) {
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);
  const navigate = useNavigate();

  // Close panel on click outside
  useEffect(() => {
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const isUnread = (notif) => !(notif.readBy || []).includes(currentUserId);

  const getIcon = (type) => {
    if (type === "delayed_shipping") return <AlertTriangle size={14} className="notif-icon-warn" />;
    return <Bell size={14} className="notif-icon-default" />;
  };

  const formatTime = (ts) => {
    if (!ts) return "";
    const d = new Date(ts);
    const now = new Date();
    const diffMs = now - d;
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return "Just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  return (
    <div className="notif-bell-wrap" ref={panelRef}>
      <button className="notif-bell-btn" onClick={() => setOpen(!open)} aria-label="Notifications">
        <Bell size={20} />
        {unreadCount > 0 && <span className="notif-badge">{unreadCount > 99 ? "99+" : unreadCount}</span>}
      </button>

      {open && (
        <div className="notif-panel">
          <div className="notif-panel-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h4>Notifications</h4>
            <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
              {unreadCount > 0 && (
                <button 
                  className="notif-clear-btn" 
                  onClick={onMarkAllRead} 
                  style={{ color: "var(--primary)", fontWeight: "600" }}
                >
                  Mark all Read
                </button>
              )}
              {notifications.length > 0 && (
                <button className="notif-clear-btn" onClick={() => { onClearAll(); setOpen(false); }}>
                  <Trash2 size={13} /> Clear All
                </button>
              )}
            </div>
          </div>

          <div className="notif-list">
            {notifications.length === 0 ? (
              <div className="notif-empty">
                <Bell size={24} className="notif-empty-icon" />
                <span>No notifications</span>
              </div>
            ) : (
              notifications.map((notif) => {
                const unread = isUnread(notif);
                return (
                  <div
                    key={notif.id}
                    className={`notif-item ${unread ? "notif-item-unread" : "notif-item-read"}`}
                    onClick={() => { 
                      if (unread) onMarkRead(notif.id); 
                      if (isTicketNotification(notif)) {
                        const targetPath = getTicketNavigationPath(notif, userRoles);
                        navigate(targetPath);
                        setOpen(false);
                        return;
                      }
                      if (notif.path) {
                        navigate(notif.path);
                        setOpen(false);
                      }
                    }}
                  >
                    <div className="notif-item-icon">{getIcon(notif.type)}</div>
                    <div className="notif-item-content">
                      <p className={`notif-item-msg ${unread ? "notif-bold" : ""}`}>{notif.message}</p>
                      <span className="notif-item-time">{formatTime(notif.createdAt)}</span>
                    </div>
                    {unread && <span className="notif-dot" />}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
